import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {
    getActualSchoolYearRequest,
    getFederalStatesRequest,
    getSchoolYearsRequest
} from "../redux/actions/jointPowersActions";
import {createEmployeeRequest} from "../redux/actions/employeeActions";
import {getOrganizationsRequest} from "../redux/actions/organizationActions";

const EmployeeForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({});

    const schoolYears = useSelector(state => state.jointPower.schoolYears);
    const isLoadingSchoolYears = useSelector(state => state.jointPower.loading.getSchoolYears);
    const actualSchoolYear = useSelector(state => state.jointPower.actualSchoolYear);
    const isLoadingActualSchoolYear = useSelector(state => state.jointPower.loading.getActualSchoolYear);
    const isLoadingCreateEmployee = useSelector(state => state.employee.loading.createEmployee);
    const federalStates = useSelector(state => state.jointPower.federalStates);
    const isLoadingFederalStates = useSelector(state => state.jointPower.loading.getFederalStates);
    const userGroups = useSelector(state => state.authentication.userGroups);
    const organizations = useSelector(state => state.organization.organizations);
    const isLoadingOrganizations = useSelector(state => state.organization.loading.getOrganizations);

    useEffect(() => {
        if (!schoolYears.length && !isLoadingSchoolYears) {
            dispatch(getSchoolYearsRequest());
        }
    }, [dispatch, schoolYears, isLoadingSchoolYears]);

    useEffect(() => {
        if (!federalStates.length && !isLoadingFederalStates) {
            dispatch(getFederalStatesRequest());
        }
    }, [dispatch, federalStates, isLoadingFederalStates]);

    useEffect(() => {
        if (!actualSchoolYear && !isLoadingActualSchoolYear) {
            dispatch(getActualSchoolYearRequest());
        } else if (actualSchoolYear && !formData.SchoolYearID) {
            setFormData(prevFormData => ({
                ...prevFormData,
                SchoolYearID: actualSchoolYear.ID
            }));
        }
    }, [dispatch, actualSchoolYear, isLoadingActualSchoolYear, formData]);

    useEffect(() => {
        if (!organizations.length && !isLoadingOrganizations) {
            dispatch(getOrganizationsRequest());
        }
    }, [dispatch, userGroups, isLoadingOrganizations, organizations]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = {};

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.Email) {
            newErrors.Email = 'E-Mail-Adresse ist erforderlich.';
        } else if (!emailRegex.test(formData.Email)) {
            newErrors.Email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
        }

        if (!formData.SchoolYearID) {
            newErrors.SchoolYearID = 'Schuljahr ist erforderlich.';
        }
        if (!formData.FederalStateID) {
            newErrors.FederalStateID = 'Bundesland der Stammschule ist erforderlich.';
        }
        if (!formData.OrganizationID) {
            newErrors.OrganizationID = 'Organisation ist erforderlich.';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            setErrors({});
            if (!isLoadingCreateEmployee) {
                dispatch(createEmployeeRequest(formData));
                setFormData({});
                setErrors({});
                navigate("/mdb/institute/overview");
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevFormData => ({
            ...prevFormData,
            [name]: value
        }));
        setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    };

    const getFieldClassName = (fieldName) => {
        return errors[fieldName] ? 'form-control is-invalid' : 'form-control';
    };

    return (
        <div className="form-container text-start">
            <form onSubmit={handleSubmit} className="employee-form">
                <div className="mb-3 form-group">
                    <label htmlFor="email" className="form-label">E-Mail-Adresse des Mitverwendeten</label>
                    <input
                        type="email"
                        className={getFieldClassName('Email')}
                        id="Email"
                        name="Email"
                        value={formData.Email || ''}
                        onChange={handleChange}
                    />
                    {errors.Email && <div className="text-danger">{errors.Email}</div>}
                </div>
                <div className="mb-3 form-group">
                    <label htmlFor="schoolYear" className="form-label">Schuljahr</label>
                    <select
                        className={errors.SchoolYearID ? 'form-select is-invalid' : 'form-select'}
                        id="SchoolYearID"
                        name="SchoolYearID"
                        value={formData.SchoolYearID || ''}
                        onChange={handleChange}
                    >
                        {schoolYears.map((year) => (
                            <option key={year.ID} value={year.ID}>
                                {year.Name}
                            </option>
                        ))}
                    </select>
                    {errors.SchoolYearID && <div className="text-danger">{errors.SchoolYearID}</div>}
                </div>
                <div className="mb-3 form-group">
                    <label htmlFor="organization" className="form-label">Organisationseinheit</label>
                    <select
                        className={errors.OrganizationID ? 'form-select is-invalid' : 'form-select'}
                        id="OrganizationID"
                        name="OrganizationID"
                        value={formData.OrganizationID || ''}
                        onChange={handleChange}
                    >
                        <option value="">Bitte wählen</option>
                        {organizations.map((org, i) => (
                            <option key={i} value={org.ID}>
                                {org.Name}
                            </option>
                        ))}
                    </select>
                    {errors.OrganizationID && <div className="text-danger">{errors.OrganizationID}</div>}
                </div>
                <div className="mb-3 form-group">
                    <label htmlFor="federalState" className="form-label">Bundesland der Stammschule</label>
                    <select
                        className={errors.FederalStateID ? 'form-select is-invalid' : 'form-select'}
                        id="FederalStateID"
                        name="FederalStateID"
                        value={formData.FederalStateID || ''}
                        onChange={handleChange}
                    >
                        <option value="">Bitte wählen</option>
                        {federalStates.map((federalState, i) => (
                            <option key={i} value={federalState.ID}>
                                {federalState.Name}
                            </option>
                        ))}
                    </select>
                    {errors.FederalStateID && <div className="text-danger">{errors.FederalStateID}</div>}
                </div>
                <div className="form-navigation-buttons">
                    <button type="submit" className="btn btn-primary">Submit</button>
                </div>
            </form>
        </div>
    );
};

export default EmployeeForm;
